import { type StyleValue, computed, defineComponent, onMounted, onUnmounted, ref } from 'vue'

import useBaseModal from '@/composables/base/modal/useBaseModal'

const globalModalCount = ref(0)

interface Props {
  tData?: string
  id?: string
  class?: string
  containerClass?: string
  containerStyle?: StyleValue
  modalkey: symbol
  overflowDisabled?: boolean
  hasOverlay?: boolean
  onOutsideClick?: () => void
}

export default defineComponent((props: Props, context) => {
  const {
    contenElm,
    position,
    overlayClass,
    transformClass,
    transformStyle,
    playRevealAnimation
  } = useBaseModal(props.modalkey)
  const tData = computed((): string => props.tData ?? 'base-modal')
  const overflowDisabled = computed((): boolean => props.overflowDisabled ?? true)
  const hasOverlay = computed((): boolean => props.hasOverlay ?? true)

  // #region Getters

  const containerClass = computed((): string => {
    if (position.value === 'right') {
      return '!justify-end'
    }

    if (position.value === 'top') {
      return 'items-start'
    }

    if (position.value === 'bottom') {
      return 'items-end'
    }

    return position.value === 'left' ? '!justify-start' : 'items-center'
  })

  // #endregion

  onMounted(() => {
    playRevealAnimation()
  })

  onMounted(() => {
    if (overflowDisabled.value) {
      globalModalCount.value++
      document.body.style.overflow = 'hidden'
    }
  })

  onUnmounted(() => {
    if (overflowDisabled.value) {
      globalModalCount.value--
      if (globalModalCount.value === 0) {
        document.body.style.overflow = 'auto'
      }
    }
  })

  return () => (
    <div
      t-data={tData.value}
      id={props.id}
      class={[
        'fixed inset-0 z-1000 size-full',
        props.class,
        hasOverlay.value ? 'pointer-events-auto' : 'pointer-events-none'
      ]}
    >
      {hasOverlay.value &&
        <div
          class={['absolute size-full bg-black transition-opacity duration-300', overlayClass.value]}
          onClick={props.onOutsideClick}
        />}
      <div
        class={[
          'pointer-events-none absolute z-1001 flex size-full justify-center',
          containerClass.value,
          props.containerClass
        ]}
        style={props.containerStyle}
      >
        <div
          ref={contenElm}
          class={['pointer-events-auto', transformClass.value]}
          style={transformStyle.value}
        >
          {context.slots.default?.()}
        </div>
      </div>
    </div>
  )
}, {
  name: 'BaseModal',
  props: ['tData', 'id', 'class', 'containerClass', 'containerStyle', 'modalkey', 'overflowDisabled', 'hasOverlay', 'onOutsideClick']
})
